import type { PendingCrucibleCutscenes } from '@sorrem/shared';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useCallback } from 'react';
import { useGetPendingCrucibleCutscenesQuery, useMarkCutsceneSeenMutation } from '@sorrem-client/ui/store';
import { useCurrentCharacterArgs, useCurrentCharacterId } from './use-current-character-id';

export interface CrucibleCutscenesState {
    /**
     * The cutscenes the current character still has to see around this run,
     * or null while loading, without a run, or if the request failed. A
     * failure deliberately reads as "nothing pending": cutscenes must never
     * keep a player out of a run.
     */
    pending: PendingCrucibleCutscenes | null;
    isLoading: boolean;
    /** Records a cutscene as watched or skipped. Failures are swallowed; it just shows again next time. */
    markSeen: (cutsceneId: string) => Promise<void>;
}

/**
 * The cutscenes around one Crucible run for the current character. Which
 * cutscenes a run has, and which are already seen, is decided by the server
 * (`GET /api/crucibles/:crucibleId/cutscenes`).
 */
export function useCrucibleCutscenes(crucibleId: string | null): CrucibleCutscenesState {
    const { characterId, isLoading: isCharacterLoading } = useCurrentCharacterId();
    const args = useCurrentCharacterArgs();
    const [markCutsceneSeen] = useMarkCutsceneSeenMutation();

    const { data, isLoading } = useGetPendingCrucibleCutscenesQuery(
        crucibleId === null || characterId === null ? skipToken : { crucibleId, characterId },
    );

    const markSeen = useCallback(async (cutsceneId: string): Promise<void> => {
        if (crucibleId === null || args === skipToken) {
            return;
        }
        try {
            await markCutsceneSeen({ ...args, crucibleId, cutsceneId }).unwrap();
        } catch {
            // Not fatal: the cutscene is simply still pending next time.
        }
    }, [args, crucibleId, markCutsceneSeen]);

    return {
        pending: data ?? null,
        // Until the character resolves the query is skipped, which RTK reports
        // as not loading; count it as loading so nobody acts on "nothing pending".
        isLoading: crucibleId !== null && (characterId === null || isCharacterLoading || isLoading),
        markSeen,
    };
}
